import { useRef, useState } from "react";
import { UploadCloud, Loader2, ChevronLeft, ChevronRight, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, photoUrl } from "../../lib/api";

const BATCH = 3;

export const PhotoManager = ({ photos, onChange }) => {
  const [busy, setBusy] = useState(null);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    let current = [...photos];
    setBusy({ done: 0, total: files.length });
    for (let i = 0; i < files.length; i += BATCH) {
      const fd = new FormData();
      files.slice(i, i + BATCH).forEach((f) => fd.append("files", f));
      try {
        const { data } = await http.post("/admin/upload", fd);
        current = [...current, ...(data?.paths || [])];
        onChange(current);
      } catch (e) {
        toast.error(errMsg(e));
      }
      setBusy({ done: Math.min(i + BATCH, files.length), total: files.length });
    }
    setBusy(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const move = (from, to) => {
    if (to < 0 || to >= photos.length || from === to) return;
    const next = [...photos];
    const [p] = next.splice(from, 1);
    next.splice(to, 0, p);
    onChange(next);
  };

  return (
    <div data-testid="photo-manager">
      <label
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (e.dataTransfer.files?.length) upload(e.dataTransfer.files); }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${over ? "border-champagne bg-champagne/10" : "border-ink/15 bg-pearl hover:border-ink/30"}`}
        data-testid="photo-dropzone"
      >
        {busy ? <Loader2 className="h-8 w-8 animate-spin text-champagne-dark" /> : <UploadCloud className="h-8 w-8 text-champagne-dark" />}
        <span className="font-medium text-ink">{busy ? `Caricamento ${busy.done}/${busy.total}…` : "Trascina qui le foto o clicca per sceglierle"}</span>
        <span className="text-xs text-slate-600">Qualsiasi formato (JPG, PNG, HEIC, WEBP, AVIF, TIFF…) · nessun limite di numero</span>
        <input ref={inputRef} type="file" multiple accept="image/*,.heic,.heif,.avif,.tif,.tiff,.bmp" className="hidden" disabled={!!busy}
          onChange={(e) => upload(e.target.files)} data-testid="photo-upload-input" />
      </label>

      {photos.length > 0 && (
        <>
          <p className="mt-4 text-xs text-slate-600">{photos.length} foto · trascina per riordinare · la prima è la copertina</p>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="photo-grid">
            {photos.map((p, i) => (
              <div key={p} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (drag !== null) move(drag, i); setDrag(null); }}
                className={`group relative aspect-[4/3] overflow-hidden rounded-xl bg-ink/5 ${drag === i ? "opacity-40" : ""}`} data-testid={`photo-item-${i}`}>
                <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
                {i === 0 && <span className="absolute left-2 top-2 rounded-full bg-champagne px-2 py-0.5 text-[10px] font-bold uppercase text-ink">Copertina</span>}
                <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 bg-gradient-to-t from-ink/80 to-transparent p-2">
                  <div className="flex gap-1">
                    <button type="button" onClick={() => move(i, i - 1)} className="photo-btn" data-testid={`photo-left-${i}`} title="Sposta a sinistra"><ChevronLeft className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => move(i, i + 1)} className="photo-btn" data-testid={`photo-right-${i}`} title="Sposta a destra"><ChevronRight className="h-3.5 w-3.5" /></button>
                    {i > 0 && <button type="button" onClick={() => move(i, 0)} className="photo-btn" data-testid={`photo-cover-${i}`} title="Imposta copertina"><Star className="h-3.5 w-3.5" /></button>}
                  </div>
                  <button type="button" onClick={() => onChange(photos.filter((_, j) => j !== i))} className="photo-btn hover:!bg-red-600" data-testid={`photo-delete-${i}`} title="Elimina"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
